import {Injectable} from '@angular/core';
import {EnergieEnum} from '../../pages/rt2012/suivi-des-consommations/enums/EnergieEnum';
import {Energie} from '../../pages/rt2012/suivi-des-consommations/models/Energie';
import {ParametrageBatiment} from '../../models/rt2012/parametrageBatiment';
import {TypeProductionEnum} from '../../models/rt2012/typeProductionEnum';
import {TypeChauffageEnum} from '../../models/rt2012/typeChauffageEnum';
import {TypeECSEnum} from '../../models/rt2012/typeECSEnum';
import {UsageOccupant} from '../../pages/rt2012/parametrage-usages/models/UsageOccupant';
import {Logement} from '../../models/logement';
import {UserService} from '../user.service';
import {User} from '../../models/user';

@Injectable({
  providedIn: 'root'
})
export class EnergieService {

  private energies: Energie[] = [];
  private user: User = null;

  constructor(private userService: UserService) {
    this.energies.push(
      new Energie(EnergieEnum.ELECTRICITE, '#F8DAA8', 'kWh'),
      new Energie(EnergieEnum.GAZ, '#F79E8E', 'kWh'),
      new Energie(EnergieEnum.EAU, '#384978', 'm³'),
      new Energie(EnergieEnum.RESEAU_CHALEUR, '#FF6C59', 'kWh'),
      new Energie(EnergieEnum.BOIS, '#5c2b0e', 'm³'),
      new Energie(EnergieEnum.FIOUL, '#2C2968', 'm³'),
      new Energie(EnergieEnum.HYBRIDE, '#788d1b', 'kWh'),
      new Energie(EnergieEnum.HYBRIDE2, '#b4d222', 'kWh'),
      new Energie(EnergieEnum.CHAUFFAGE, '#5f1f5e', 'kWh'),
      new Energie(EnergieEnum.ECS, '#3796b3', 'kWh'),
      new Energie(EnergieEnum.REFROIDISSEMENT, '#2C2968', ''),
    );
    this.user = userService.data;
  }
  public getEnergies(): Energie[] {
    return this.energies;
  }

  public getUnite(energie: EnergieEnum): string {
    return this.energies.find(e => e.type === energie).unite;
  }

  public getColor(energie: EnergieEnum): string {
    return this.energies.find(e => e.type === energie).color;
  }

  public getEnergiesEditables(logement: Logement, usageOccupant: UsageOccupant = null): EnergieEnum[] {
    let energiesAvailables  = this.getEnergiesAvailables(logement, usageOccupant);

    if (this.hasEnedisAccessToken(logement)){
      energiesAvailables = energiesAvailables.filter(energie => energie !== EnergieEnum.ELECTRICITE);
    }

    if (this.hasGazparAccessToken(logement)){
      energiesAvailables = energiesAvailables.filter(energie => energie !== EnergieEnum.GAZ);
    }

    return energiesAvailables;
  }

  public getEnergiesAvailables(logement: Logement, usageOccupant: UsageOccupant = null): EnergieEnum[] {
    const parametrageBatiment = logement.parametrageBatiment;
    if (usageOccupant === null){
      usageOccupant = logement.usageOccupant;
    }
    const energiesAvailable = new Set<EnergieEnum>();

    if (null !== this.user && (logement.canRT2012Repartition() || this.hasEnedisAccessToken(logement))) {
      energiesAvailable.add(EnergieEnum.ELECTRICITE);
    }

    if (null !== this.user && this.hasGazparAccessToken(logement)) {
      energiesAvailable.add(EnergieEnum.GAZ);
    }

    if (this.hasChauffageGaz(parametrageBatiment)) {
      energiesAvailable.add(EnergieEnum.GAZ);
    }
    if (this.hasECSGaz(parametrageBatiment)) {
      energiesAvailable.add(EnergieEnum.GAZ);
    }
    if (this.hasChauffageFioul(parametrageBatiment)) {
      energiesAvailable.add(EnergieEnum.FIOUL);
    }
    if (usageOccupant?.cuissonPlaqueGazFixe || usageOccupant?.cuissonFourGaz) {
      energiesAvailable.add(EnergieEnum.GAZ);
    }
    if (null !== parametrageBatiment && parametrageBatiment.compteursIndividuelsChauffage && parametrageBatiment.typeProductionChauffage === TypeProductionEnum.COLLECTIF){
      energiesAvailable.add(EnergieEnum.CHAUFFAGE);
    }
    if (null !== parametrageBatiment && parametrageBatiment.compteursIndividuelsEcs && parametrageBatiment.typeProductionEcs === TypeProductionEnum.COLLECTIF){
      energiesAvailable.add(EnergieEnum.ECS);
    }

    return [...energiesAvailable];
  }

  public hasChauffageGaz(parametrageBatiment: ParametrageBatiment) {
    return parametrageBatiment?.typeProductionChauffage === TypeProductionEnum.INDIVIDUEL &&
      [
        TypeChauffageEnum.POMPE_CHALEUR_GAZ,
        TypeChauffageEnum.CHAUDIERE_GAZ
      ].includes(parametrageBatiment.typeChauffage);
  }

  public hasChauffageElec(parametrageBatiment: ParametrageBatiment) {
    return parametrageBatiment?.typeProductionChauffage === TypeProductionEnum.INDIVIDUEL &&
      [
        TypeChauffageEnum.CONVECTEURS,
        TypeChauffageEnum.POMPE_CHALEUR_ELEC,
      ].includes(parametrageBatiment.typeChauffage);
  }

  public hasECSGaz(parametrageBatiment: ParametrageBatiment) {
    return parametrageBatiment?.typeProductionEcs === TypeProductionEnum.INDIVIDUEL &&
      [
        TypeECSEnum.CHAUDIERE_GAZ,
        TypeECSEnum.POMPE_CHALEUR_GAZ,
        TypeECSEnum.BALLON_SOLAIRE_IND_GAZ
      ].includes(parametrageBatiment.typeEcs);
  }

  public hasChauffageFioul(parametrageBatiment: ParametrageBatiment) {
    return parametrageBatiment?.typeProductionChauffage === TypeProductionEnum.INDIVIDUEL &&
      [
        TypeECSEnum.CHAUDIERES_FIOUL,
      ].includes(parametrageBatiment.typeEcs);
  }

  private hasEnedisAccessToken(logement: Logement){
    return this.user && this.user.enedisAccessTokens.filter(enedisAccessToken => enedisAccessToken.logement.id === logement.id).length > 0;
  }

  private hasGazparAccessToken(logement: Logement){
    return this.user && this.user.gazparAccessTokens.filter(gazparAccessToken => gazparAccessToken.logement.id === logement.id).length > 0;
  }
}
